import * as React from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = ({
  open,
  onOpenChange,
  children,
  className,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
  className?: string
}) => {
  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md transition-all duration-300"
        onClick={() => onOpenChange?.(false)}
      />
      {/* Dialog container */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6 pointer-events-none">
        {/* Modal content — flex column, scrollable body, sticky footer */}
        <div 
          className={cn(
            "relative w-full max-w-lg flex flex-col max-h-[90vh] bg-[#0c0c0c] border border-stone-800 rounded-[32px] shadow-2xl animate-in zoom-in-95 duration-300 pointer-events-auto",
            className
          )} 
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </>
  )
}

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left px-8 pt-8 pb-5 border-b border-stone-800",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-bold leading-none tracking-tight text-stone-100",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = "DialogTitle"

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-stone-500 leading-relaxed", className)}
    {...props}
  />
))
DialogDescription.displayName = "DialogDescription"

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 overflow-y-auto px-8 py-8", className)}
    {...props}
  />
))
DialogContent.displayName = "DialogContent"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-3 px-8 py-6 border-t border-stone-800 bg-stone-900/20 rounded-b-[32px]",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogCloseButton = ({ onClick }: { onClick?: () => void }) => (
  <button
    onClick={onClick}
    className="absolute right-8 top-8 rounded-full p-2 text-stone-500 hover:bg-stone-800 hover:text-stone-200 transition-all duration-200"
  >
    <X className="h-5 w-5" />
    <span className="sr-only">Close</span>
  </button>
)

export {
  Dialog,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogCloseButton,
}
